import { type CollectionSummaryJSONObject, isSystemTag } from '@ikegps/core-api';
import { IkePromiseQueue } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { Logger, Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import type { AllStatuses } from './status.selector.ctrl';

export class CollectionStatusModalCtrl {
	collections: CollectionSummaryJSONObject[];
	pq = new IkePromiseQueue(Logger, 10);

	@observable settingStatus: boolean = false;

	constructor(collections: CollectionSummaryJSONObject[]) {
		makeObservable(this);

		this.collections = collections;
	}

	async SetStatusCollections(status: string | null, allStatuses: AllStatuses[]) {
		if (this.settingStatus) {
			return;
		}
		this.setSettingStatus(true);
		const newCollectionUuids = [];
		for (const collection of this.collections) {
			const allStatusesForCollection = allStatuses.find((e) => e.collectionId === collection.objectId);
			if (allStatusesForCollection) {
				if (status === null || allStatusesForCollection.statuses.indexOf(status) > -1) {
					this.pq.add(async () => {
						const uuid = await this.updateCollectionStatus(collection, allStatusesForCollection, status);
						newCollectionUuids.push(uuid);
					});
				}
			}
		}
		await this.pq.join();
		await CurrentState.updateInspectedCollection();
		await this.refresh(newCollectionUuids);
		this.setSettingStatus(false);
	}

	private async updateCollectionStatus(
		collection: CollectionSummaryJSONObject,
		allStatusesForCollection: AllStatuses,
		status: string | null
	): Promise<string> {
		const rest = Services.Department.rest;
		const collectionJson = await rest.Collection.getByRevision(collection.revision).fetch();
		collectionJson.data.data[allStatusesForCollection.statusFieldId] = status ? [status.split(' > ')] : [];
		const newCollection = await rest.save(rest.Objects.Collection, collectionJson);
		return newCollection.uuid;
	}

	private async refresh(collectionUuids: string[]) {
		const collectionSummaries = await Promise.all(
			collectionUuids.map((uuid) => AppStore.CollectionSummary.getFetchableByUuid(uuid).refetch())
		);
		return Promise.all([
			AppStore.Tag.refresh(),
			...this.getTagIds(collectionSummaries).map((tagId) => AppStore.TagSummary.getFetchable(tagId).refetch()),
		]);
	}

	private getTagIds(collectionSummaries: CollectionSummaryJSONObject[]): string[] {
		const tags: { [id: string]: boolean } = {};

		for (const collectionSummary of collectionSummaries) {
			for (const tag of collectionSummary.tags) {
				if (!tags[tag] && !isSystemTag(tag)) {
					tags[tag] = true;
				}
			}
		}

		return Object.keys(tags);
	}

	@action private setSettingStatus(settingStatus: boolean) {
		this.settingStatus = settingStatus;
	}

	getActualStatuses() {
		const statuses = [];
		for (const summary of CurrentState.Tag.collections) {
			if (summary.status === 'undefined' || summary.status === '') {
				statuses.push('undefined');
			} else {
				statuses.push(summary.status);
			}
		}
		const result = statuses.reduce(function (allStatuses, status) {
			if (status in allStatuses) {
				allStatuses[status]++;
			} else {
				allStatuses[status] = 1;
			}
			return allStatuses;
		}, {});

		return result;
	}
}
